import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { allDapps } from '../data/dapps';
import { CalendarIcon, Upload, Check, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const preselectedDapp = searchParams.get('dapp') || '';
  const applicationType = searchParams.get('type') || '';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    teamName: '',
    country: '',
    website: '',
    github: '',
    linkedin: '',
    previousProjects: '',
    selectedDapp: preselectedDapp || (applicationType === 'custom' ? 'Custom Proposal' : ''),
    customProposalName: '',
    customProposalDescription: '',
    smartContract: '',
    scalability: '',
    securityPlan: '',
    techStack: '',
    mvpDate: null,
    betaDate: null,
    mainnetDate: null,
    incentivePreference: '',
    whyUni2: '',
    fileName: '',
    fileUrl: ''
  });

  // Date picker states
  const [mvpOpen, setMvpOpen] = useState(false);
  const [betaOpen, setBetaOpen] = useState(false);
  const [mainnetOpen, setMainnetOpen] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // MOCK: In production, this would upload to S3
    toast.info('File upload is simulated (MOCK). In production, files will be uploaded to S3.');
    
    handleInputChange('fileName', file.name);
    handleInputChange('fileUrl', `https://mock-s3-bucket.com/${file.name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.teamName || !formData.country || !formData.selectedDapp) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        team_info: {
          team_name: formData.teamName,
          country: formData.country,
          website: formData.website || null,
          github: formData.github || null,
          linkedin: formData.linkedin || null,
          previous_projects: formData.previousProjects || null
        },
        selected_dapp: formData.selectedDapp,
        custom_proposal_name: formData.selectedDapp === 'Custom Proposal' ? formData.customProposalName : null,
        custom_proposal_description: formData.selectedDapp === 'Custom Proposal' ? formData.customProposalDescription : null,
        technical_architecture: {
          smart_contract: formData.smartContract || null,
          scalability: formData.scalability || null,
          security_plan: formData.securityPlan || null,
          tech_stack: formData.techStack || null
        },
        timeline: {
          mvp: formData.mvpDate ? format(formData.mvpDate, 'yyyy-MM') : null,
          beta: formData.betaDate ? format(formData.betaDate, 'yyyy-MM') : null,
          mainnet: formData.mainnetDate ? format(formData.mainnetDate, 'yyyy-MM') : null
        },
        incentive_preference: formData.incentivePreference || 'grant',
        why_uni2: formData.whyUni2 || null,
        file_url: formData.fileUrl || null,
        file_name: formData.fileName || null
      };

      await axios.post(`${API}/proposals`, payload);
      
      setSubmitted(true);
      toast.success(t('form.success'));
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(t('form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#000000]">
        <Navbar />
        <div className="pt-24 pb-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('form.success')}</h2>
              <p className="text-slate-300 mb-6">
                Your application has been submitted and is now under review by the comité técnico de We·R2.
              </p>
              <p className="text-sm text-slate-400">
                Note: Email confirmation is currently MOCKED. In production, you would receive an email confirmation.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000]" data-testid="application-page">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="form-headline">
              {t('form.headline')}
            </h1>
            <p className="text-slate-300">
              Fill out the form below to apply for building on We·R2 Chain
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Team Information */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.teamInfo')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="teamName" className="text-slate-300">{t('form.teamName')} *</Label>
                  <Input
                    id="teamName"
                    value={formData.teamName}
                    onChange={(e) => handleInputChange('teamName', e.target.value)}
                    placeholder="Enter team name"
                    required
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-team-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country" className="text-slate-300">{t('form.country')} *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Enter country"
                    required
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-country"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website" className="text-slate-300">{t('form.website')}</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-website"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="github" className="text-slate-300">{t('form.github')}</Label>
                  <Input
                    id="github"
                    value={formData.github}
                    onChange={(e) => handleInputChange('github', e.target.value)}
                    placeholder="https://github.com/..."
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-github"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin" className="text-slate-300">{t('form.linkedin')}</Label>
                  <Input
                    id="linkedin"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/..."
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-linkedin"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <Label htmlFor="previousProjects" className="text-slate-300">{t('form.previousProjects')}</Label>
                <Textarea
                  id="previousProjects"
                  value={formData.previousProjects}
                  onChange={(e) => handleInputChange('previousProjects', e.target.value)}
                  placeholder="Describe your team's previous Web3 projects..."
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  data-testid="input-previous-projects"
                />
              </div>
            </div>

            {/* Selected dApp */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.selectedDapp')}</h2>
              
              <Select 
                value={formData.selectedDapp} 
                onValueChange={(value) => handleInputChange('selectedDapp', value)}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-dapp">
                  <SelectValue placeholder={t('form.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  <SelectItem value="Custom Proposal" className="text-white hover:bg-white/10">
                    {t('form.customProposal')}
                  </SelectItem>
                  {allDapps.map((dapp) => (
                    <SelectItem key={dapp.id} value={dapp.title} className="text-white hover:bg-white/10">
                      {dapp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formData.selectedDapp === 'Custom Proposal' && (
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Proposal Name *</Label>
                    <Input
                      value={formData.customProposalName}
                      onChange={(e) => handleInputChange('customProposalName', e.target.value)}
                      placeholder="Enter your proposal name"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="input-custom-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Proposal Description *</Label>
                    <Textarea
                      value={formData.customProposalDescription}
                      onChange={(e) => handleInputChange('customProposalDescription', e.target.value)}
                      placeholder="Describe your proposal in detail..."
                      className="bg-white/5 border-white/10 text-white min-h-[120px]"
                      data-testid="input-custom-description"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Technical Architecture */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.techArchitecture')}</h2>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.smartContract')}</Label>
                  <Textarea
                    value={formData.smartContract}
                    onChange={(e) => handleInputChange('smartContract', e.target.value)}
                    placeholder="Describe your smart contract architecture..."
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-smart-contract"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.scalability')}</Label>
                  <Textarea
                    value={formData.scalability}
                    onChange={(e) => handleInputChange('scalability', e.target.value)}
                    placeholder="How will your solution scale?"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-scalability"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.securityPlan')}</Label>
                  <Textarea
                    value={formData.securityPlan}
                    onChange={(e) => handleInputChange('securityPlan', e.target.value)}
                    placeholder="Describe your security measures..."
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-security"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.techStack')}</Label>
                  <Input
                    value={formData.techStack}
                    onChange={(e) => handleInputChange('techStack', e.target.value)}
                    placeholder="e.g., React, Node.js, Solidity, Hardhat"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-tech-stack"
                  />
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.timeline')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.mvp')}</Label>
                  <Popover open={mvpOpen} onOpenChange={setMvpOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-white/5 border-white/10 text-white hover:bg-white/10"
                        data-testid="date-mvp"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.mvpDate ? format(formData.mvpDate, 'MMM yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0B0F19] border-white/10">
                      <Calendar
                        mode="single"
                        selected={formData.mvpDate}
                        onSelect={(date) => {
                          handleInputChange('mvpDate', date);
                          setMvpOpen(false);
                        }}
                        className="bg-[#0B0F19] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.beta')}</Label>
                  <Popover open={betaOpen} onOpenChange={setBetaOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-white/5 border-white/10 text-white hover:bg-white/10"
                        data-testid="date-beta"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.betaDate ? format(formData.betaDate, 'MMM yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0B0F19] border-white/10">
                      <Calendar
                        mode="single"
                        selected={formData.betaDate}
                        onSelect={(date) => {
                          handleInputChange('betaDate', date);
                          setBetaOpen(false);
                        }}
                        className="bg-[#0B0F19] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('form.mainnet')}</Label>
                  <Popover open={mainnetOpen} onOpenChange={setMainnetOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-white/5 border-white/10 text-white hover:bg-white/10"
                        data-testid="date-mainnet"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.mainnetDate ? format(formData.mainnetDate, 'MMM yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0B0F19] border-white/10">
                      <Calendar
                        mode="single"
                        selected={formData.mainnetDate}
                        onSelect={(date) => {
                          handleInputChange('mainnetDate', date);
                          setMainnetOpen(false);
                        }}
                        className="bg-[#0B0F19] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Incentive Preference */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.incentivePref')}</h2>
              
              <RadioGroup
                value={formData.incentivePreference}
                onValueChange={(value) => handleInputChange('incentivePreference', value)}
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
                data-testid="radio-incentive"
              >
                {['grant', 'revenueShare', 'hybrid', 'strategic'].map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem value={option} id={option} className="border-white/20 text-primary" />
                    <Label htmlFor={option} className="text-slate-300 cursor-pointer">{t(`form.${option}`)}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Why We·R2 Chain */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.whyUni2')}</h2>
              <Textarea
                value={formData.whyUni2}
                onChange={(e) => handleInputChange('whyUni2', e.target.value)}
                placeholder="Why do you want to build on We·R2 Chain?"
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                data-testid="input-why-uni2"
              />
            </div>

            {/* File Upload */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">{t('form.fileUpload')}</h2>
              
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx"
                  onChange={handleFileUpload}
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-300 mb-2">
                    {formData.fileName || 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-slate-500">PDF, DOC, DOCX, PPT, PPTX (max 50MB)</p>
                </label>
              </div>
              <p className="text-xs text-yellow-500/80 mt-2">
                Note: File upload is currently MOCKED. Files will be stored in S3 in production.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full glow-primary"
              disabled={isSubmitting}
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('form.submitting')}
                </>
              ) : (
                t('form.submit')
              )}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
